import { useState, useRef, useCallback, useEffect } from 'react';
import { message as antdMessage } from 'antd';

/**
 * Custom hook for managing voice recording functionality
 * Handles MediaRecorder, audio visualization, and recording state
 */
export const useVoiceRecording = ({ onSendMessage, handleFileUpload, handleError, t }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordSeconds, setRecordSeconds] = useState(0);
    const [pendingVoice, setPendingVoice] = useState(null); // { blob, url, type, fileName }

    // Refs for media recorder
    const mediaRecorderRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const mediaChunksRef = useRef([]);
    const recordTimerRef = useRef(null);
    const isPausedRef = useRef(false);
    const cancelRequestedRef = useRef(false);

    // Refs for audio visualization
    const waveRef = useRef(null);
    const wavePathRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const dataArrayRef = useRef(null);
    const animationFrameRef = useRef(null);
    const sourceNodeRef = useRef(null);

    const formatTime = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const seconds = (totalSeconds % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    const getPreferredAudioMime = () => {
        if (!window.MediaRecorder || typeof window.MediaRecorder.isTypeSupported !== 'function') return '';
        const types = [
            'audio/webm;codecs=opus',  // Best: explicit audio, widely supported (Chrome, Firefox, Edge)
            'audio/webm',
            'audio/ogg;codecs=opus',   // Good: unambiguous audio format
            'audio/ogg',
            'audio/aac',               // Fallback: explicit audio
            'audio/wav',               // Fallback: lossless but large
            'audio/mp4'                // Last resort: Safari fallback, may be confused with video
        ];
        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) return type;
        }
        return '';
    };

    const stopStreamTracks = (stream) => {
        try {
            stream?.getTracks?.().forEach(t => t.stop());
        } catch (e) { }
    };

    const startRecording = useCallback(async () => {
        if (isRecording) return;

        // Check if MediaRecorder is supported
        if (!window.MediaRecorder || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            const msg = t('audio_recording_not_supported');
            antdMessage.error(msg === 'audio_recording_not_supported' ? 'Audio recording is not supported on this device.' : msg);
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            const mimeType = getPreferredAudioMime();
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            mediaRecorderRef.current = recorder;
            mediaChunksRef.current = [];

            recorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    mediaChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = async () => {
                if (cancelRequestedRef.current) {
                    // Discard recording without creating a pending voice preview
                    cancelRequestedRef.current = false;
                    mediaChunksRef.current = [];
                    setRecordSeconds(0);
                    return;
                }
                const containerType = recorder.mimeType || mimeType || 'audio/mp4';
                const finalType = containerType;
                const blob = new Blob(mediaChunksRef.current, { type: finalType });
                mediaChunksRef.current = [];
                let ext = 'mp4';
                const lowerType = finalType.toLowerCase();
                if (lowerType.includes('webm')) {
                    ext = lowerType.startsWith('audio/') ? 'weba' : 'webm';
                } else if (lowerType.includes('aac')) {
                    ext = 'aac';
                } else if (lowerType.includes('ogg')) {
                    ext = 'ogg';
                } else if (lowerType.includes('wav')) {
                    ext = 'wav';
                } else if (lowerType.includes('mp4')) {
                    ext = 'm4a'; // Force m4a for audio/mp4 to avoid being treated as video
                }
                const fileName = `voice_${Date.now()}.${ext}`;
                const url = URL.createObjectURL(blob);
                setPendingVoice({ blob, url, type: finalType, fileName });
                setRecordSeconds(0);
                // Cleanup analyser/animation after stop
                try {
                    if (animationFrameRef.current) {
                        cancelAnimationFrame(animationFrameRef.current);
                        animationFrameRef.current = null;
                    }
                    if (sourceNodeRef.current) {
                        try { sourceNodeRef.current.disconnect(); } catch (e) { }
                    }
                    if (audioContextRef.current) {
                        try { audioContextRef.current.close(); } catch (e) { }
                    }
                } catch (e) { }
                analyserRef.current = null;
                sourceNodeRef.current = null;
                audioContextRef.current = null;
                dataArrayRef.current = null;
            };

            recorder.start();
            setIsRecording(true);
            setRecordSeconds(0);
            if (recordTimerRef.current) clearInterval(recordTimerRef.current);
            recordTimerRef.current = setInterval(() => {
                if (!isPausedRef.current) {
                    setRecordSeconds(prev => prev + 1);
                }
            }, 1000);

            // Setup analyser for simple amplitude-reactive sine wave
            try {
                const AudioCtx = window.AudioContext || window.webkitAudioContext;
                const audioCtx = new AudioCtx();
                audioContextRef.current = audioCtx;
                const source = audioCtx.createMediaStreamSource(stream);
                sourceNodeRef.current = source;
                const analyser = audioCtx.createAnalyser();
                analyser.fftSize = 1024;
                analyserRef.current = analyser;
                const bufferLength = analyser.fftSize;
                dataArrayRef.current = new Uint8Array(bufferLength);
                source.connect(analyser);

                const update = () => {
                    try {
                        const analyserNode = analyserRef.current;
                        const dataArray = dataArrayRef.current;
                        const wavePath = wavePathRef.current;
                        if (!analyserNode || !dataArray || !wavePath) {
                            animationFrameRef.current = requestAnimationFrame(update);
                            return;
                        }
                        analyserNode.getByteTimeDomainData(dataArray);
                        let sum = 0;
                        for (let i = 0; i < dataArray.length; i++) {
                            const v = dataArray[i] - 128;
                            sum += Math.abs(v);
                        }
                        const avg = sum / dataArray.length;
                        const W = 600;
                        const H = 24;
                        const mid = H / 2;
                        const silenceThreshold = 2;
                        if (avg <= silenceThreshold) {
                            wavePath.setAttribute('d', `M0 ${mid} L${W} ${mid}`);
                        } else {
                            const points = 120;
                            const stepX = W / (points - 1);
                            let d = '';
                            for (let i = 0; i < points; i++) {
                                const idx = Math.min(dataArray.length - 1, Math.floor(i * dataArray.length / points));
                                const v = (dataArray[idx] - 128) / 128;
                                const y = mid + v * (H / 2 - 1);
                                const x = i * stepX;
                                d += (i === 0 ? 'M' : 'L') + x + ' ' + y + ' ';
                            }
                            wavePath.setAttribute('d', d.trim());
                        }
                    } catch (e) { }
                    animationFrameRef.current = requestAnimationFrame(update);
                };
                animationFrameRef.current = requestAnimationFrame(update);
            } catch (e) { }
        } catch (error) {
            console.error('Recording start error:', error);
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                const msg = t('microphone_permission_denied');
                antdMessage.error(msg === 'microphone_permission_denied' ? 'Microphone permission denied. Please enable it in settings.' : msg);
            } else {
                handleError(error);
            }
            stopStreamTracks(mediaStreamRef.current);
            mediaStreamRef.current = null;
            setIsRecording(false);
        }
    }, [isRecording, handleError, t]);

    const pauseRecording = useCallback(() => {
        try {
            const recorder = mediaRecorderRef.current;
            if (recorder && recorder.state === 'recording' && typeof recorder.pause === 'function') {
                recorder.stop();
                setIsRecording(false);
                if (recordTimerRef.current) {
                    clearInterval(recordTimerRef.current);
                    recordTimerRef.current = null;
                }
                stopStreamTracks(mediaStreamRef.current);
                mediaStreamRef.current = null;
            }
        } catch (e) { }
    }, []);

    const clearPendingVoice = useCallback(() => {
        try { if (pendingVoice?.url) URL.revokeObjectURL(pendingVoice.url); } catch (e) { }
        setPendingVoice(null);
    }, [pendingVoice]);

    const sendPendingVoice = useCallback(async () => {
        if (!pendingVoice?.blob) return;
        try {
            let ext = 'mp4';
            let finalMimeType = pendingVoice.type || pendingVoice.blob.type || 'audio/mp4';

            if (finalMimeType.includes('webm')) {
                ext = finalMimeType.toLowerCase().startsWith('audio/') ? 'weba' : 'webm';
            } else if (finalMimeType.includes('ogg')) {
                ext = 'ogg';
            } else if (finalMimeType.includes('wav')) {
                ext = 'wav';
            } else if (finalMimeType.includes('mp4')) {
                ext = 'm4a';
                finalMimeType = 'audio/m4a'; // Force explicit audio mime for upload
            }

            const fallbackName = `voice_${Date.now()}.${ext}`;
            const file = new File([pendingVoice.blob], pendingVoice.fileName || fallbackName, {
                type: finalMimeType,
                lastModified: Date.now()
            });

            const uploadedFile = await handleFileUpload(file, { linkToConversation: false });
            if (uploadedFile && uploadedFile.fileId) {
                onSendMessage({ message: '', attachment_ids: [uploadedFile.fileId] });
                clearPendingVoice();
            }
        } catch (error) {
            handleError(error);
        }
    }, [pendingVoice, handleError, onSendMessage, clearPendingVoice, handleFileUpload]);

    // Cleanup on unmount
    useEffect(() => () => {
        if (recordTimerRef.current) clearInterval(recordTimerRef.current);
        stopStreamTracks(mediaStreamRef.current);
        try { if (pendingVoice?.url) URL.revokeObjectURL(pendingVoice.url); } catch (e) { }
    }, []);

    return {
        isRecording,
        recordSeconds,
        pendingVoice,
        waveRef,
        wavePathRef,
        startRecording,
        pauseRecording,
        clearPendingVoice,
        sendPendingVoice,
        formatTime
    };
};
